"use client";

import type { VariantProps } from "class-variance-authority";
import NextLink from "next/link";
import {
	type CSSProperties,
	type ForwardedRef,
	type HTMLAttributeAnchorTarget,
	type MouseEvent,
	forwardRef,
	useRef,
	useState,
} from "react";
import { buttonStyles } from "../../forms/Button/Button";
import { ExternalLinkIcon } from "../../icons/ExternalLinkIcon";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../overlay/Tooltip";
import { classNames } from "../../utils/classNames";

export type LinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
	children?: React.ReactNode;
	href: string;
	className?: string;
	size?: "sm" | "md";
	target?: HTMLAttributeAnchorTarget;
	isDisabled?: boolean;
	isExternal?: boolean;
	variant?: VariantProps<typeof buttonStyles>["intent"];
	intent?: "primary" | "secondary" | "tertiary";
	truncate?: boolean;
	tooltipOnOverflow?: boolean;
	maxWidth?: CSSProperties["maxWidth"];
	prefetch?: boolean | "auto" | null;
};

const LinkInner = (
	{
		children,
		href,
		className,
		target,
		size = "md",
		isDisabled,
		isExternal,
		variant,
		intent = "primary",
		truncate = false,
		tooltipOnOverflow = false,
		maxWidth,
		style,
		onMouseEnter,
		onMouseLeave,
		prefetch = false,
		...rest
	}: LinkProps,
	forwardedRef: ForwardedRef<HTMLAnchorElement>,
) => {
	const localRef = useRef<HTMLAnchorElement | null>(null);
	const [isTooltipOpen, setIsTooltipOpen] = useState(false);

	const setRefs = (node: HTMLAnchorElement | null) => {
		localRef.current = node;

		if (typeof forwardedRef === "function") {
			forwardedRef(node);
		} else if (forwardedRef) {
			(forwardedRef as React.MutableRefObject<HTMLAnchorElement | null>).current = node;
		}
	};

	const handleMouseEnter = (event: MouseEvent<HTMLAnchorElement>) => {
		onMouseEnter?.(event);

		if (!tooltipOnOverflow) {
			return;
		}

		const linkElement = localRef.current;
		setIsTooltipOpen(Boolean(linkElement && (linkElement.offsetWidth ?? 0) < (linkElement.scrollWidth ?? 0)));
	};

	const handleMouseLeave = (event: MouseEvent<HTMLAnchorElement>) => {
		onMouseLeave?.(event);

		if (tooltipOnOverflow) {
			setIsTooltipOpen(false);
		}
	};

	const linkStyle = maxWidth !== undefined ? { ...style, maxWidth } : style;
	const title = rest.title;

	const linkClassName = variant
		? classNames(
				buttonStyles({ intent: variant, size }),
				"min-w-4",
				isDisabled ? "cursor-not-allowed opacity-50" : "",
				truncate ? "inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap align-bottom" : "",
				className,
			)
		: classNames(
				"link",
				`link--${size}`,
				intent === "primary" ? "" : `link--${intent}`,
				truncate ? "inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap align-bottom" : "",
				className,
			);

	const content = isExternal ? (
		<>
			{children} <ExternalLinkIcon className="mb-0.5 inline h-3 w-3" />
		</>
	) : (
		children
	);

	const isPlainAnchorHref =
		href.startsWith("tel:") || href.startsWith("mailto:") || href.startsWith("blob:") || href.startsWith("data:");

	const resolvedTarget = isExternal ? "_blank" : target;
	const resolvedRel = resolvedTarget === "_blank" ? "noopener noreferrer" : undefined;

	const element = isPlainAnchorHref ? (
		<a
			href={href}
			ref={setRefs}
			target={resolvedTarget}
			rel={resolvedRel}
			className={linkClassName}
			style={linkStyle}
			title={title}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onClick={isDisabled ? (e: MouseEvent<HTMLAnchorElement>) => e.preventDefault() : undefined}
			aria-disabled={isDisabled ? "true" : undefined}
			{...rest}
		>
			{content}
		</a>
	) : (
		<NextLink
			href={href}
			ref={setRefs}
			target={resolvedTarget}
			rel={resolvedRel}
			className={linkClassName}
			style={linkStyle}
			title={title}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onClick={isDisabled ? (e: MouseEvent<HTMLAnchorElement>) => e.preventDefault() : undefined}
			aria-disabled={isDisabled ? "true" : undefined}
			prefetch={prefetch}
			{...rest}
		>
			{content}
		</NextLink>
	);

	return tooltipOnOverflow ? (
		<Tooltip open={isTooltipOpen} placement="top-start">
			<TooltipTrigger>{element}</TooltipTrigger>
			<TooltipContent>{children}</TooltipContent>
		</Tooltip>
	) : (
		element
	);
};

LinkInner.displayName = "Link";

export const Link = forwardRef(LinkInner);
